<script setup lang="ts">
import type { ShipexkursTab } from '~~/types';
import type { GameVehicleSummary, WikiVehicle, WikiVehicleUexPrice } from "~~/types/gamedata";

const props = defineProps({
  vehicle: {
    type: Object as () => GameVehicleSummary,
    required: true,
  },
  raw: {
    type: Object as () => WikiVehicle,
    required: true,
  },
});

function formatDuration(minutes: number): string {
  const totalSeconds = Math.round(minutes * 60);
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;

  return `${mins}m ${secs}s`;
}

type PriceField = 'price_buy' | 'price_rent';

function validOffers(offers: WikiVehicleUexPrice[] | null | undefined, field: PriceField): WikiVehicleUexPrice[] {
  return (offers ?? []).filter((offer) => {
    const price = offer[field];
    return typeof price === 'number' && Number.isFinite(price) && price > 0;
  });
}

function starSystemName(offer: WikiVehicleUexPrice): string {
  return offer.starmap_location?.star_system_name?.trim() || 'Unbekanntes System';
}

function terminalName(offer: WikiVehicleUexPrice): string {
  return offer.terminal_name?.trim() || 'Unbekanntes Terminal';
}

function quickViewLocation(offer: WikiVehicleUexPrice): string {
  return `${starSystemName(offer)} · ${terminalName(offer)}`;
}

function offerLink(offer: WikiVehicleUexPrice): string | null {
  return offer.starmap_location?.web_url?.trim() || offer.starmap_location?.link?.trim() || null;
}

function bestOffersBySystem(offers: WikiVehicleUexPrice[], field: PriceField): WikiVehicleUexPrice[] {
  const best = new Map<string, WikiVehicleUexPrice>();

  for (const offer of offers) {
    const system = starSystemName(offer);
    const current = best.get(system);
    if (!current || Number(offer[field]) < Number(current[field])) best.set(system, offer);
  }

  return [...best.entries()]
    .sort(([left], [right]) => left.localeCompare(right, 'de'))
    .map(([, offer]) => offer);
}

const purchaseOffers = computed(() => validOffers(props.raw.uex_prices?.purchase, 'price_buy'));
const rentalOffers = computed(() => validOffers(props.raw.uex_prices?.rental, 'price_rent'));
const hasInGamePrices = computed(() => purchaseOffers.value.length > 0 || rentalOffers.value.length > 0);

function inGameItems() {
  return [
    ...(purchaseOffers.value.length ? [{
      label: 'KAUFEN:',
      value: '',
      full: true as const,
    }, ...bestOffersBySystem(purchaseOffers.value, 'price_buy').map((offer) => ({
      label: quickViewLocation(offer),
      value: String(formatCurrency(offer.price_buy)),
      suffix: 'aUEC',
    }))] : []),
    ...(rentalOffers.value.length ? [{
      label: 'MIETEN (1 TAG):',
      value: '',
      full: true as const,
    }, ...bestOffersBySystem(rentalOffers.value, 'price_rent').map((offer) => ({
      label: quickViewLocation(offer),
      value: String(formatCurrency(offer.price_rent)),
      suffix: 'aUEC',
    }))] : []),
  ];
}

const curated = computed(() => props.raw.curated_data);

/** "$600 (ursprünglich: $350)" — Klammerzusatz nur, wenn ein davon abweichender Ursprungspreis bekannt ist. */
function formatPriceWithOriginal(current: number | null | undefined, original: number | null | undefined): string | null {
  if (current == null) return null;
  const currentLabel = `$${formatCurrency(current)}`;
  if (original == null || original === current) return currentLabel;
  return `${currentLabel} (orginal: $${formatCurrency(original)})`;
}

function pledgePriceItems() {
  const items: ShipexkursTab['items'] = [];

  const pledgePrice = formatPriceWithOriginal(props.vehicle.msrp, curated.value?.original_pledge_price);
  if (pledgePrice) {
    items.push({ label: 'Aktueller Pledge-Preis', value: pledgePrice });
  }

  // `curated_data.original_warbond_price` trägt trotz des Namens den aktuell
  // gültigen Warbond-Preis (verifiziert gegen die Live-Anzeige im Pledge Store)
  // — `raw.warbond_price` bleibt als bevorzugte Quelle, falls die API das
  // künftig direkt befüllt.
  const warbondPrice = props.raw.warbond_price ?? curated.value?.original_warbond_price;
  if (warbondPrice != null) {
    items.push({ label: 'Aktueller Warbond-Preis', value: `$${formatCurrency(warbondPrice)}` });
  }

  const availability = getPledgeAvailabilityLabel(curated.value?.pledge_availability);
  if (availability) {
    items.push({
      label: 'Verfügbarkeit',
      value: availability,
      full: true,
    });
  }

  return items;
}

const tabs = computed<ShipexkursTab[]>(() => [
  {
    label: 'Pledge',
    icon: 'i-lucide-wallet',
    items: pledgePriceItems(),
  },
  ...(hasInGamePrices.value ? [{
    label: 'In-Game',
    icon: 'i-lucide-currency',
    items: inGameItems(),
  }] : []),
  ...(props.raw.uex_prices ? [{
    label: 'Versicherung',
    icon: 'i-lucide-shield',
    items: [{
      label: 'Dauer',
      value: String(formatDuration(props.raw.insurance?.claim_time ?? 0)),
      third: true,
    }, {
      label: 'Beschleunigt',
      value: String(formatDuration(props.raw.insurance?.expedite_time ?? 0)),
      third: true,
    }, {
      label: 'Beschleunigungskosten',
      value: String(formatCurrency(props.raw.insurance?.expedite_cost)),
      suffix: 'aUEC',
      third: true,
    }],
  }] : []),
]);
</script>

<template>
  <ShipexkursShipTable :tabs="tabs">
    <template #tab-body="{ tab }">
      <div v-if="tab?.label === 'In-Game'" class="@container space-y-5">
        <div v-if="purchaseOffers.length">
          <p class="mb-2 text-xs font-semibold uppercase tracking-wide text-aris-400">Kaufen</p>
          <div class="space-y-2">
            <div
              v-for="offer in bestOffersBySystem(purchaseOffers, 'price_buy')"
              :key="`buy-${quickViewLocation(offer)}`"
              class="flex flex-col gap-1 py-1 @sm:flex-row @sm:items-center @sm:justify-between @sm:gap-3"
            >
              <a
                v-if="offerLink(offer)"
                :href="offerLink(offer)!"
                target="_blank"
                rel="noopener noreferrer"
                class="text-sm text-neutral-300 hover:text-aris-400 hover:underline"
              >{{ quickViewLocation(offer) }}</a>
              <span v-else class="text-sm text-neutral-300">{{ quickViewLocation(offer) }}</span>
              <span class="shrink-0 text-sm font-medium text-white @sm:text-right">
                {{ formatCurrency(offer.price_buy) }} <span class="text-xs text-dimmed">aUEC</span>
              </span>
            </div>
          </div>
        </div>
        <div v-if="rentalOffers.length">
          <p class="mb-2 text-xs font-semibold uppercase tracking-wide text-aris-400">Mieten (1 Tag)</p>
          <div class="space-y-2">
            <div
              v-for="offer in bestOffersBySystem(rentalOffers, 'price_rent')"
              :key="`rent-${quickViewLocation(offer)}`"
              class="flex flex-col gap-1 py-1 @sm:flex-row @sm:items-center @sm:justify-between @sm:gap-3"
            >
              <a
                v-if="offerLink(offer)"
                :href="offerLink(offer)!"
                target="_blank"
                rel="noopener noreferrer"
                class="text-sm text-neutral-300 hover:text-aris-400 hover:underline"
              >{{ quickViewLocation(offer) }}</a>
              <span v-else class="text-sm text-neutral-300">{{ quickViewLocation(offer) }}</span>
              <span class="shrink-0 text-sm font-medium text-white @sm:text-right">
                {{ formatCurrency(offer.price_rent) }} <span class="text-xs text-dimmed">aUEC</span>
              </span>
            </div>
          </div>
        </div>
      </div>
      <ShipexkursShipTableTabContent v-else :data="tab?.items" />
    </template>
    <template #tab-footer="{ tab }">
      <ShipexkursShipHeroPriceListSlideover
        v-if="tab?.label === 'In-Game'"
        :vehicle-name="vehicle.name"
        :purchase-offers="purchaseOffers"
        :rental-offers="rentalOffers"
      />
    </template>
  </ShipexkursShipTable>
</template>
